import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconPartyPopper,
  IconLightningBolt,
  IconScale,
  IconHelpingHand,
  IconInfoCircleOutline,
  IconArrowRight,
} from '@iwoca/icons';
import { Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';

import styles from './ResultScreen.module.css';
import { useLocale } from '../../../hooks/useLocale';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { ChoiceCards } from '../components/ChoiceCards';
import { ExitToSignupLink } from '../components/ExitToSignupLink';
import { NumberField } from '../components/NumberField';
import { OUTCOMES, TOutcomeKey } from '../logic/outcomes.config';
import { QUESTIONS } from '../logic/questions.config';
import { answeredCount } from '../logic/scoring';
import { TAnswers } from '../logic/types';

const OUTCOME_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  trophy: IconPartyPopper,
  spark: IconLightningBolt,
  compass: IconScale,
  heart: IconHelpingHand,
  info: IconInfoCircleOutline,
};

type TProps = {
  result: { key: TOutcomeKey; meter: number };
  answers: TAnswers;
  onRestart: () => void;
};

export const ResultScreen = ({ result, answers, onRestart }: TProps) => {
  const { t } = useLingui();
  const { i18n } = useLocale();
  const navigate = useNavigate();
  const outcome = OUTCOMES[result.key];
  const OutcomeIcon = OUTCOME_ICONS[outcome.icon];
  const count = answeredCount(answers);
  const meter = result.meter;

  return (
    <div className={styles.container}>
      <div
        className={`${styles.outcomeHead} ${styles[`accent_${outcome.accent}`] ?? ''}`}
      >
        <span className={styles.chip}>{i18n._(outcome.chip)}</span>
        <div className={styles.titleRow}>
          {OutcomeIcon && <OutcomeIcon className={styles.outcomeIcon} />}
          <h2 className={styles.outcomeTitle}>{i18n._(outcome.title)}</h2>
        </div>
        <p className={styles.outcomeBody}>{i18n._(outcome.body)}</p>
      </div>

      {count > 0 && (
        <div className={styles.meterSection}>
          <div className={styles.meterTrack}>
            <div
              className={styles.meterFill}
              style={{ width: `${result.meter}%` }}
            />
          </div>
          <span className={styles.meterLabel}>{t`Assessment: ${meter}%`}</span>
        </div>
      )}

      <ul className={styles.points}>
        {outcome.points.map((point) => {
          const text = i18n._(point);
          return (
            <li key={text} className={styles.point}>
              {text}
            </li>
          );
        })}
      </ul>

      <div className={styles.recap}>
        <h3 className={styles.recapHeading}>
          <Trans>Your details</Trans>
        </h3>
        {QUESTIONS.map((question) => {
          const currentValue = answers[question.id];
          if (currentValue === undefined) return null;
          const questionTitle = i18n._(question.title);
          return (
            <div key={question.id} className={styles.recapRow}>
              <span className={styles.recapLabel}>{questionTitle}</span>
              <div className={styles.recapInput}>
                {question.kind === 'choice' && question.choices && (
                  <ChoiceCards
                    choices={question.choices}
                    value={
                      typeof currentValue === 'string'
                        ? currentValue
                        : undefined
                    }
                    groupLabel={questionTitle}
                    disabled
                  />
                )}
                {question.kind === 'number' && (
                  <NumberField
                    value={
                      typeof currentValue === 'number'
                        ? currentValue
                        : undefined
                    }
                    suffix={
                      question.suffix ? i18n._(question.suffix) : undefined
                    }
                    ariaLabel={questionTitle}
                    disabled
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Button
        variant="primary"
        href="/kreditwunsch/neu"
        className={styles.signupCta}
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
          // Client-side nav so the in-memory DE locale survives the handoff;
          // a full reload re-resolves locale from the cookie, which is unset outside .de hosts.
          event.preventDefault();
          tracking.ampli.creditCheckResultCtaClicked({
            credit_check_outcome: result.key,
            meter: result.meter,
            ...getAmplitudeDefaultEventProperties(),
          });
          navigate('/kreditwunsch/neu');
        }}
      >
        {i18n._(outcome.cta)}
        <IconArrowRight />
      </Button>

      <Button
        variant="tertiary"
        onClick={onRestart}
        className={styles.restartButton}
        type="button"
      >
        <Trans>Start over</Trans>
      </Button>

      <ExitToSignupLink phase="result" />
    </div>
  );
};
